import React, { useState } from 'react';
import { useGet } from 'restful-react';

import './ProjectCardContainer.scss';

import NewProjectCard from '../Cards/NewProjectCard/NewProjectCard';
import ProjectCard from '../Cards/ProjectCard/ProjectCard';
import { ProjectSummaries, ProjectSummary } from '../../../types/ProjectSummary';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import ProjectSummaryCard from '../Cards/ProjectSummaryCard/ProjectSummaryCard';
import { OkResponse } from '../../../common/interfaces';
import { CardTypeEnum } from '../../../types/enums/CardTypeEnum';
import { ProjectOverviewViewEnum } from '../../../types/enums/ProjectOverviewViewEnum';

type ProjectCardContainerProps = {
  activeProjectView: ProjectOverviewViewEnum;
  onChangeProjectView: (activeView: ProjectOverviewViewEnum) => void;
};

type FetchProjectsProps = {
  apiPath: string;
  cardType: CardTypeEnum;
};

const ProjectCardContainer = ({
  activeProjectView,
  onChangeProjectView,
}: ProjectCardContainerProps) => {
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const HandleShowProjectSummary = (projectId: string) => {
    setSelectedProjectId(projectId);
    onChangeProjectView(ProjectOverviewViewEnum.Summary);
  };

  const HandleProjectSummaryBackButton = () => {
    setSelectedProjectId('');
    onChangeProjectView(ProjectOverviewViewEnum.Previous);
  };

  const FetchProjects = ({ apiPath, cardType }: FetchProjectsProps) => {
    const { data: projects, loading, error } = useGet({
      path: apiPath,
      resolve: (projects: OkResponse<ProjectSummaries>) => projects.result,
    });

    if (loading) {
      return <LoadingSpinner />;
    }

    if (error) {
      return <ErrorMessage message={t('error-messages.generic-error')} />;
    }

    return (
      <>
        {projects &&
          projects.length > 0 &&
          projects.map((project: ProjectSummary) => (
            <ProjectCard
              cardType={cardType}
              project={project}
              key={project.id}
              onShowProjectSummary={HandleShowProjectSummary}
            />
          ))}
      </>
    );
  };

  const renderSwitch = (activeView: ProjectOverviewViewEnum) => {
    switch (activeView) {
      case ProjectOverviewViewEnum.Active:
        return (
          <div className="project-card-wrapper">
            <NewProjectCard />
            <FetchProjects apiPath="/projects/active" cardType={CardTypeEnum.Active} />
          </div>
        );
      case ProjectOverviewViewEnum.Previous:
        return (
          <div className="project-card-wrapper">
            <FetchProjects apiPath="/projects/previous" cardType={CardTypeEnum.Previous} />
          </div>
        );
      case ProjectOverviewViewEnum.Summary:
        return (
          <ProjectSummaryCard
            projectId={selectedProjectId}
            onBackButton={HandleProjectSummaryBackButton}
          />
        );
      default:
        return null;
    }
  };

  return <div className="project-card-container">{renderSwitch(activeProjectView)}</div>;
};

export default ProjectCardContainer;
